import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {Link} from "react-router";

const FEED_URL = "https://feeds.simplecast.com/xZFNy8N_";
const STYLE_ID = "player-gallery-keyframes";

const PLAY_SVG  = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="26" height="26"><path fill="white" d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm115.7 272l-176 101c-15.8 8.8-35.7-2.5-35.7-21V152c0-18.4 19.8-29.8 35.7-21l176 107c16.4 9.2 16.4 32.9 0 42z"/></svg>`;
const PAUSE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="26" height="26"><path fill="white" d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm-16 328c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v160zm112 0c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v160z"/></svg>`;
const SPIN_SVG  = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="26" height="26"><style>@keyframes pgs{to{transform:rotate(360deg)}}.pgsp{transform-origin:25px 25px;animation:pgs 0.7s linear infinite}</style><circle class="pgsp" cx="25" cy="25" r="20" fill="none" stroke="white" stroke-width="5" stroke-linecap="round" stroke-dasharray="90 60"/></svg>`;

const CSS = `
    @keyframes pg-scroll {
        0%   { transform: translateX(0); }
        100% { transform: translateX(-50%); }
    }
    .pg-wrapper {
        overflow: hidden;
        background-color: #0a0a0a;
        padding: 48px 0;
    }
    .pg-track {
        display: flex;
        flex-wrap: nowrap;
        animation: pg-scroll 38s linear infinite;
        will-change: transform;
    }
    .pg-track.pg-paused {
        animation-play-state: paused;
    }
    .pg-card {
        flex: 0 0 auto;
        width: 260px;
        margin: 0 14px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
    }
    .pg-card__img {
        width: 260px;
        height: 260px;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 6px 24px rgba(0,0,0,0.6);
        flex-shrink: 0;
    }
    .pg-card__img img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
    .pg-card__title {
        color: #ccc;
        font-size: 13px;
        text-align: center;
        line-height: 1.4;
        max-width: 240px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .pg-card__btn {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        line-height: 0;
        opacity: 0.85;
        transition: opacity 0.2s, transform 0.2s;
    }
    .pg-card__btn:hover {
        opacity: 1;
        transform: scale(1.12);
    }
`;

function PlayerGallery() {
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [playingGuid, setPlayingGuid] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const audioRef = useRef(null);
    const suppressPauseRef = useRef(false);

    useEffect(() => {
        if (!document.getElementById(STYLE_ID)) {
            const el = document.createElement("style");
            el.id = STYLE_ID;
            el.textContent = CSS;
            document.head.appendChild(el);
        }

        axios
            .get(FEED_URL, {
                responseType: "text",
                headers: { Accept: "application/rss+xml, application/xml, text/xml" },
            })
            .then((res) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(res.data, "text/xml");
                const nodes = Array.from(doc.getElementsByTagName("item")).slice(0, 10);

                const list = nodes
                    .map((node) => {
                        const itunesImg = node.getElementsByTagName("itunes:image")[0];
                        const mediaThumb = node.getElementsByTagName("media:thumbnail")[0];
                        const enclosure = node.getElementsByTagName("enclosure")[0];
                        const guid = (node.getElementsByTagName("guid")[0]?.textContent || "").trim();
                        const title = (node.getElementsByTagName("title")[0]?.textContent || "").trim();
                        const href =
                            itunesImg?.getAttribute("href") ||
                            mediaThumb?.getAttribute("url") ||
                            "";
                        const audioSrc = enclosure?.getAttribute("url") || "";
                        return { guid: guid || title, title, href, audioSrc };
                    })
                    .filter((ep) => ep.href && ep.audioSrc);

                setEpisodes(list);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlaying = () => { setIsPlaying(true);  setIsBuffering(false); };
        const onPause   = () => {
            if (suppressPauseRef.current) { suppressPauseRef.current = false; return; }
            setIsPlaying(false);
            setIsBuffering(false);
        };
        const onWaiting = () => { setIsBuffering(true); };
        const onEnded   = () => { setIsPlaying(false); setIsBuffering(false); setPlayingGuid(null); };

        audio.addEventListener("playing", onPlaying);
        audio.addEventListener("pause",   onPause);
        audio.addEventListener("waiting", onWaiting);
        audio.addEventListener("ended",   onEnded);

        return () => {
            audio.removeEventListener("playing", onPlaying);
            audio.removeEventListener("pause",   onPause);
            audio.removeEventListener("waiting", onWaiting);
            audio.removeEventListener("ended",   onEnded);
        };
    }, []);

    const handleClick = (ep) => {
        const audio = audioRef.current;
        if (!audio) return;

        if (playingGuid === ep.guid) {
            if (audio.paused) {
                audio.play();
            } else {
                audio.pause();
            }
        } else {
            suppressPauseRef.current = true;
            audio.pause();
            // pause event bazen fırlamamayabiliyor (src yokken); her halükarda sıfırla
            setTimeout(() => { suppressPauseRef.current = false; }, 0);
            audio.src = ep.audioSrc;
            setPlayingGuid(ep.guid);
            setIsBuffering(true);
            setIsPlaying(false);
            audio.load();
            audio.play().catch(() => {});
        }
    };

    const isActive = playingGuid !== null && (isPlaying || isBuffering);

    const getIcon = (ep) => {
        if (playingGuid === ep.guid) {
            if (isPlaying)   return PAUSE_SVG;  // çalıyor → pause göster
            if (isBuffering) return SPIN_SVG;   // henüz başlamadı → spinner
        }
        return PLAY_SVG;
    };

    const track = episodes.length > 0 ? [...episodes, ...episodes] : [];

    // <audio> her zaman DOM'da olmalı — useEffect ilk render'da çalışır,
    // o sırada null dönülürse element hiç mount olmaz ve listener'lar takılmaz.
    return (
        <section
            className="team-and-counter py-120 bg-img"
            style={{
                backgroundImage: "url(./assets/images/thumb/team-bg.png)",
                backgroundColor: "#0a0a0a",
                display: loading || episodes.length === 0 ? "none" : undefined,
            }}
        >
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio ref={audioRef} preload="none" />

            {/* Başlık: container içinde */}
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="section-heading style-two">
                            <h5 className="section-heading__subtitle" style={{color: "#fff"}}>
                                <span className="section-heading__bars bars-one"/> PODCAST{" "}
                                <span className="section-heading__bars bars-two"/>
                            </h5>
                            <h2 className="section-heading__title" style={{color: "white"}}>
                                <span className="section-heading__title-colored">SON</span>{" "}EKLENENLER
                            </h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* Slider: container DIŞINDA, tam genişlik */}
            <div style={{overflow: "hidden", marginTop: 32}}>
                <div className={`pg-track${isActive ? " pg-paused" : ""}`}>
                    {track.map((ep, idx) => (
                        <div key={`${ep.guid}-${idx}`} className="pg-card">
                            <div className="pg-card__img">
                                <img src={ep.href} alt={ep.title} />
                            </div>
                            <span className="pg-card__title">{ep.title}</span>
                            <button
                                className="pg-card__btn"
                                onClick={() => handleClick(ep)}
                                aria-label={playingGuid === ep.guid && isPlaying ? "Duraklat" : "Oynat"}
                                dangerouslySetInnerHTML={{ __html: getIcon(ep) }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Alt link: container içinde */}
            <div className="container" style={{marginTop: 24}}>
                <br/><br/>
                <div style={{float: "right"}}>
                    <h5 className="section-heading__subtitle" style={{color: "#fff"}}>
                        <span className="section-heading__bars bars-one"/>
                        {" "}<Link to="/podcastler" reloadDocument>TÜM PODCASTLER</Link>{" "}
                        <span className="section-heading__bars bars-two"/>
                    </h5>
                </div>
            </div>
        </section>
    );
}

export default PlayerGallery;